<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onMount, onDestroy, mount, unmount } from 'svelte';
    import { __ } from '@oat-sa-private/ui-core';
    import settingsKeys from '../../../settings/settingsKeys.js';

    import ControlRow from './generic/ControlRow.svelte';
    import ColourSwitcher from './generic/ColourSwitcher.svelte';
    import CursorSvg from './util/CursorSvg.svelte';

    /**
     * @typedef {Object} Props
     * @property {Object} initialState
     * @property {Object} areaBroker
     * @property {Function} onChange
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        initialState,
        areaBroker,
        onChange
    } = $props();

    // svelte-ignore state_referenced_locally
    const targetElement = areaBroker?.getContainer();

    // colors
    const defaultColorKey = 'default';
    const colors = {
        default: {
            label: __('Default'),
            fill: 'white',
            stroke: 'black'
        },
        white: {
            label: __('White'),
            fill: 'white',
            stroke: 'black'
        },
        black: {
            label: __('Black'),
            fill: 'black',
            stroke: 'white'
        },
        yellow: {
            label: __('Yellow'),
            fill: 'yellow',
            stroke: 'black'
        },
        green: {
            label: __('Green'),
            fill: 'lime',
            stroke: 'black'
        },
        red: {
            label: __('Red'),
            fill: 'red',
            stroke: 'black'
        }
    };
    let colorSelected = $state(defaultColorKey);

    const normalizedColors = Object.keys(colors).map(key => ({
        // properties for ColourSwitcher
        key,
        label: colors[key].label,
        colour: `--cursor-color-${key}`,
        // properties for colorHandler
        id: key
    }));

    const sizes = {
        pointer: [
            { w: 12, h: 16 },
            { w: 22, h: 32 },
            { w: 44, h: 64 },
            { w: 66, h: 96 },
            { w: 88, h: 128 }
        ],
        hand: [
            { w: 14, h: 16 },
            { w: 29, h: 32 },
            { w: 58, h: 64 },
            { w: 87, h: 96 },
            { w: 116, h: 128 }
        ]
    };

    const minStepperValue = 0;
    const maxStepperValue = sizes.pointer.length - 1;
    const step = 1;
    let stepperValue = $state(0);

    let cursorSvgProps = $state({});
    let cursorSvg = null;

    function addCursor(position) {
        if (!cursorSvg) {
            // Create the component if it doesn't exist yet
            cursorSvgProps = {
                size: sizes.pointer[stepperValue],
                color: colors[colorSelected],
                targetElement,
                position
            };
            cursorSvg = mount(CursorSvg, {
                target: targetElement,
                props: cursorSvgProps
            });
            cursorSvg.addSvgCursor();
        } else {
            // Or change its type
            cursorSvgProps.size = sizes.pointer[stepperValue];
            cursorSvgProps.color = colors[colorSelected];
            cursorSvgProps.position = position;
            cursorSvg.addSvgCursor();
        }
    }

    function removeCursor() {
        if (cursorSvg) {
            unmount(cursorSvg);
            cursorSvg = null;
        }
    }

    function handleMouseChange(position) {
        if (colorSelected === defaultColorKey && stepperValue === minStepperValue) {
            removeCursor();
            return;
        }
        addCursor(position);
    }

    // svelte-ignore state_referenced_locally
    let nonDefault = initialState?.nonDefault;

    /**
     * Handle the input being changed
     * @param {Object} [newValue]
     */
    function handleMouseSizeChange(newValue = {}) {
        stepperValue = newValue?.value;
        const position = newValue?.position;

        handleMouseChange(position);

        onChange?.({
            key: settingsKeys.mousePointer,
            state: {
                size: sizes.pointer[stepperValue].h,
                color: colorSelected,
                nonDefault: stepperValue !== minStepperValue
            }
        });
    }

    /**
     * Handle the input being changed
     * @param {Object} [newValue]
     */
    function handleMouseColorChange(newValue = {}) {
        colorSelected = newValue?.value;
        const position = newValue?.position;

        handleMouseChange(position);

        onChange?.({
            key: settingsKeys.mousePointer,
            state: {
                size: sizes.pointer[stepperValue].h,
                color: colorSelected,
                nonDefault: colorSelected !== defaultColorKey
            }
        });
    }

    onMount(() => {
        if (initialState?.color) {
            handleMouseColorChange({ value: initialState.color });
        }
        if (initialState?.size) {
            let stepperValueForSize = sizes.pointer.findIndex(s => s.h === initialState.size);
            if (stepperValueForSize < 0) {
                stepperValueForSize = minStepperValue;
            }
            handleMouseSizeChange({ value: stepperValueForSize });
        }
    });

    onDestroy(() => {
        removeCursor();
    });
</script>

<style>
    :root {
        /* A11y cursor */
        --cursor-color-white: #ffffff;
        --cursor-color-black: #000000;
        --cursor-color-yellow: #ffff00;
        --cursor-color-green: #00ff00;
        --cursor-color-red: #ff0000;
    }

    .mouse-pointer-setting :global(.select.lite button) {
        border-bottom: 0;
    }
</style>

<div
    class="mouse-pointer-setting"
    class:changed-control={colorSelected !== defaultColorKey || stepperValue !== minStepperValue}>
    <ColourSwitcher
        value={colorSelected}
        options={normalizedColors}
        defaultOptionKey={defaultColorKey}
        onChange={handleMouseColorChange}
        name="contrast-pointer-picker" />
    <ControlRow
        icon="pointer-size-16"
        label={__('Size')}
        min={minStepperValue}
        max={maxStepperValue}
        {step}
        {nonDefault}
        value={stepperValue}
        ariaLabelDecr={__('decrease pointer size')}
        ariaLabelIncr={__('increase pointer size')}
        onChange={handleMouseSizeChange} />
</div>
